import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CheckBox from '@node-space/storybook-components/dist/CheckBox'
import { Option as SelectOption } from '@node-space/storybook-components/dist/Select'
import SelectGroup from '@node-space/storybook-components/dist/SelectGroup'
import { TextAction } from '@node-space/storybook-components/dist/TextAction'
import { Box, Icon } from '@node-space/ui-components'
import { VerificationRoutes } from 'constants/verification'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useVerticalContext } from 'hooks/context/useVerticalContext'
import { useBusinessDetailsMutation } from 'hooks/mutations/useBusinessDetailsMutation'
import { useConfirmIndustryPreference } from 'hooks/useConfirmIndustryPreference'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { FormSubmitButton } from 'pages/Verification/_components/FormSubmitButton'
import { FormSubmitError } from 'pages/Verification/_components/FormSubmitError'
import { StepDescription } from 'pages/Verification/_components/StepDescription'
import { BaseErrorResponse } from 'types/errors'
import { BusinessDetailsPayload } from 'types/verification'
import { getSelectGroupFilterOptions } from 'utils/forms'

export const ConfirmIndustryPrompt = () => {
  const { t } = useTranslation()
  const { setActiveRoute, dynamicFields } = useVerificationContext()
  const { requestError, resetRequestError, handleRequestError } = useMappedErrorMessage()
  const { industryOptions, businessDetails, setBusinessDetails } = useVerticalContext()

  const { mutate: putBusinessDetails, isPending: isSubmittingBusinessDetails } =
    useBusinessDetailsMutation()
  const { isUpdatingIndustryPreference, onUpdateIndustryPreference } =
    useConfirmIndustryPreference()

  const [industryValue, setIndustryValue] = useState(businessDetails?.industryReference)
  const [isChecked, setIsChecked] = useState(false)

  const isLoadingSubmit = useMemo(
    () => isSubmittingBusinessDetails || isUpdatingIndustryPreference,
    [isSubmittingBusinessDetails, isUpdatingIndustryPreference]
  )

  const introductionLink = (
    <TextAction
      text={t('uploadDocuments.confirmVertical.introductionLink')}
      onClick={() => setActiveRoute(VerificationRoutes.BUSINESS_DETAILS)}
    />
  )
  const introduction = (
    <>
      {t('uploadDocuments.confirmVertical.introductionPre')} {introductionLink}{' '}
      {t('uploadDocuments.confirmVertical.introductionPost')}
    </>
  )

  const handleFilterIndustries = (option: SelectOption, inputValue: string) =>
    getSelectGroupFilterOptions(option, inputValue, industryOptions)

  const handleConfirmIndustryVertical = () => {
    onUpdateIndustryPreference({
      value: true,
      onError: error => handleRequestError(error, 'putVerticalPreference'),
    })
  }

  const handleContinue = () => {
    resetRequestError()
    if (industryValue !== businessDetails?.industryReference) {
      const updatedBusinessDetails: BusinessDetailsPayload = {
        ...businessDetails,
        industryReference: industryValue,
      }
      putBusinessDetails(updatedBusinessDetails, {
        onSuccess: () => {
          setBusinessDetails(updatedBusinessDetails)
          dynamicFields?.refetch()
          handleConfirmIndustryVertical()
        },
        onError: (error: BaseErrorResponse) => {
          handleRequestError(error, 'putBusinessDetails')
        },
      })
      return
    }
    handleConfirmIndustryVertical()
  }

  return (
    <Box flexCol gap={16}>
      <StepDescription
        paragraphs={[introduction, t('uploadDocuments.confirmVertical.pleaseConfirm')]}
      />
      {requestError?.show && (
        <FormSubmitError
          errorCode={requestError?.errorCode}
          sentryTraceId={requestError?.sentryTraceId}
        />
      )}
      <SelectGroup
        label={t('verification.industry')}
        options={industryOptions}
        value={industryValue}
        onChange={value => setIndustryValue(value)}
        isSearchable
        iconLeft={<Icon name="MagnifyingGlass" />}
        customFilter={handleFilterIndustries}
      />
      <Box paddingY={8}>
        <CheckBox
          label={t('uploadDocuments.confirmVertical.checkboxLabel')}
          value={isChecked}
          onChange={() => setIsChecked(current => !current)}
        />
      </Box>
      <FormSubmitButton disabled={!isChecked} loading={isLoadingSubmit} onClick={handleContinue} testId='continue-button' />
    </Box>
  )
}
